import { PF_HW } from '../../sim/constants';
import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E-M4 — FAUCHE. Le professeur de la trajectoire ANGULAIRE.
 *
 *  Il descend en zigzag SEC — pas l'ondulation douce d'un Éclat, mais un
 *  va-et-vient triangulaire qui file plein travers puis s'inverse net, tout en
 *  lâchant un tir visé de loin en loin.
 *
 *  ── Ce qu'il enseigne ──
 *  À anticiper une inversion plutôt qu'à suivre une position. Une sinusoïde
 *  prévient de son demi-tour en ralentissant ; un zigzag ne prévient de rien —
 *  il va à pleine vitesse jusqu'au point de rupture, puis repart. Le joueur qui
 *  vise l'ennemi « où il est » tire toujours un temps en retard ; celui qui a lu
 *  la période sait où sera le point d'inversion et l'y attend. C'est la lecture
 *  d'un rythme, pas d'une courbe.
 *
 *  ── Pourquoi un hook, et pas une trajectoire du moteur (PLAN.md §3.3) ──
 *  Le moteur connaît la sinusoïde (douce) et la traversée (une seule direction),
 *  aucune ne donne ce triangle net qui rebrousse. Plutôt que d'ajouter une forme
 *  de mouvement au moteur pour un seul ennemi, on l'exprime par le hook
 *  `onUpdate` — dernier recours documenté, soumis aux mêmes règles que la
 *  simulation : pur, sans allocation, sans Math transcendante (ici, seul
 *  `Math.floor` — exact au bit près — sert à replier le temps sur la période).
 *
 *  Léger et vite abattu : sa leçon est la trajectoire, pas l'encaissement. */

/** Aller-retour complet du zigzag, en secondes. */
const ZIG_PERIOD = 1.7;
/** Vitesse latérale du zigzag, en u/s. Sèche mais sous la vitesse du joueur, qui
 *  peut donc toujours se replacer par rapport à lui. */
const ZIG_SPEED = 210;
/** Marge au bord : le zigzag ne colle jamais le mur, où il coincerait le joueur
 *  contre la paroi sans échappée. */
const ZIG_LIMIT = PF_HW - 40;

export default defineEnemy({
  id: 'fauche',
  rank: Rank.Standard,
  hp: 90,
  visualRadius: 22,
  score: 150,
  telegraph: 0.8,
  drops: 1,
  // La descente vient du moteur (Linear) ; le hook n'ajoute QUE le zigzag
  // latéral. On ne réécrit jamais ce que le moteur sait déjà faire.
  motion: { kind: Motion.Linear, speed: 200 },
  emitter: {
    pattern: Pattern.Aimed,
    every: 1.4,
    count: 1,
    speed: 400,
    firstDelay: 1.0,
    family: Fam.EnemyRed,
  },
  onUpdate: (slot, age, dt, ctx) => {
    // Onde triangulaire : `t` est la phase repliée sur la période (Math.floor,
    // seule transcendante autorisée car exacte), `dir` bascule à mi-période.
    // On INTÈGRE un déplacement latéral au lieu de poser une position absolue :
    // l'ennemi part de la case de son couloir et zigzague autour, sans saut à la
    // naissance. La dérive éventuelle est bornée par l'écrêtage aux flancs.
    const t = age - Math.floor(age / ZIG_PERIOD) * ZIG_PERIOD;
    const dir = t < ZIG_PERIOD * 0.5 ? 1 : -1;
    let x = ctx.x[slot]! + dir * ZIG_SPEED * dt;
    if (x > ZIG_LIMIT) x = ZIG_LIMIT;
    else if (x < -ZIG_LIMIT) x = -ZIG_LIMIT;
    ctx.x[slot] = x;
  },
});
